/**
 * Paper detection via an on-screen guide box + centre-out expansion.
 *
 * UX contract: user sees a paper-shaped dashed rectangle (the "guide") and
 * aligns the page inside it. Visually the guide is the target they hit.
 *
 * Internally, we don't trust the whole guide box as "paper" — a user's face,
 * hand, or hair often creeps into the guide's corners. Instead we carve out a
 * smaller **trust rect** at the dead centre of the guide (half its width and
 * height), sample the seed colour there, and grow the bbox outward from the
 * trust rect one row/column at a time. This genuinely expands from the centre.
 *
 * Why seed from a small trust rect:
 *   - Pollution-proof: face/hand/hair near the guide edges never touches the
 *     seed average.
 *   - Honest expansion: because we start *inside* the trust rect, the first
 *     strip outward is already paper in well-positioned frames, so expansion
 *     actually happens instead of stalling at the guide edge.
 *
 * Pipeline:
 *   1. Compute guide rect (visual UX) and trust rect (inner 50% — internal).
 *   2. Luminance + Sobel (for edge-contrast metric + debug rendering).
 *   3. Seed mean RGB from the trust rect.
 *   4. Start bbox = trust rect; march outward in each axis. A strip is
 *      accepted if ≥ stripAcceptRatio of its pixels match the seed
 *      (max per-channel distance ≤ colorTolerance).
 *   5. Require the grown bbox to COVER the guide (paper fills the advertised
 *      region) + frame-fill / expansion gates.
 */

export interface PaperDetectResult {
  score: number;
  detected: boolean;
  debugImageData: ImageData;
  bbox: { x: number; y: number; w: number; h: number };
  bboxPx: { minX: number; minY: number; maxX: number; maxY: number };
  frameFill: number;
  bboxFill: number;
  edgeContrast: number;
}

export interface GuideDetectParams {
  /** Max per-channel RGB distance from the seed colour. Lower = stricter match. */
  colorTolerance: number;
  /** Min Sobel magnitude to render in the debug viz (doesn't affect detection). */
  gradientThreshold: number;
  /** Fraction of pixels in a candidate strip that must match the signature to keep growing. */
  stripAcceptRatio: number;
  /** Guide box width as fraction of frame (centred). */
  guideW: number;
  /** Guide box height as fraction of frame (centred). */
  guideH: number;
  /** Bbox must cover at least this fraction of the frame before we call it a detection. */
  minFillRatio: number;
  /** Min expansion past the guide (summed in both axes, normalised to available room). */
  minExpansion: number;
  /** How far (as fraction of guide dim) the bbox is allowed to fall short of each
   *  guide edge while still counting as "covering" it. 0 = strict, 0.2 = 20% slop. */
  guideCoverMargin: number;
}

// Paper-shaped defaults: on a 4:3 preview, 0.42 × 0.75 renders as portrait
// aspect ≈ 0.75 (close to US letter / A4), not a square.
export const DEFAULT_GUIDE_PARAMS: GuideDetectParams = {
  colorTolerance: 20,
  gradientThreshold: 60,
  stripAcceptRatio: 0.5,
  guideW: 0.42,
  guideH: 0.75,
  minFillRatio: 0.25,
  minExpansion: 0,
  guideCoverMargin: 0.15,
};

// Trust rect is half of the guide in each dimension (so quarter of its area),
// centred. Big enough to give a stable colour average; small enough that a
// user's face/hand touching the guide edge never pollutes the seed.
const TRUST_FRAC = 0.5;

export function detectPaperGuide(
  imageData: ImageData,
  params: GuideDetectParams = DEFAULT_GUIDE_PARAMS,
): PaperDetectResult {
  const { data, width, height } = imageData;
  const {
    colorTolerance,
    gradientThreshold,
    stripAcceptRatio,
    guideW,
    guideH,
    minFillRatio,
    minExpansion,
    guideCoverMargin,
  } = params;
  const total = width * height;

  // ── Step 1: guide rect + trust rect ──────────────────────────────────────
  const gw = Math.max(8, Math.round(width * guideW));
  const gh = Math.max(8, Math.round(height * guideH));
  const gx0 = Math.floor((width - gw) / 2);
  const gy0 = Math.floor((height - gh) / 2);
  const gx1 = gx0 + gw - 1;
  const gy1 = gy0 + gh - 1;

  const tw = Math.max(4, Math.floor(gw * TRUST_FRAC));
  const th = Math.max(4, Math.floor(gh * TRUST_FRAC));
  const tx0 = Math.floor((width - tw) / 2);
  const ty0 = Math.floor((height - th) / 2);
  const tx1 = tx0 + tw - 1;
  const ty1 = ty0 + th - 1;

  // ── Step 2: luminance + Sobel (L1) ───────────────────────────────────────
  const lum = new Float32Array(total);
  for (let i = 0; i < total; i++) {
    const o = i * 4;
    lum[i] = 0.299 * data[o] + 0.587 * data[o + 1] + 0.114 * data[o + 2];
  }
  const grad = new Float32Array(total);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const tl = lum[(y - 1) * width + (x - 1)];
      const tc = lum[(y - 1) * width + x];
      const tr = lum[(y - 1) * width + (x + 1)];
      const ml = lum[y * width + (x - 1)];
      const mr = lum[y * width + (x + 1)];
      const bl = lum[(y + 1) * width + (x - 1)];
      const bc = lum[(y + 1) * width + x];
      const br = lum[(y + 1) * width + (x + 1)];
      const gx = -tl + tr - 2 * ml + 2 * mr - bl + br;
      const gy = -tl - 2 * tc - tr + bl + 2 * bc + br;
      grad[y * width + x] = Math.abs(gx) + Math.abs(gy);
    }
  }

  // ── Step 3: seed signature from the trust rect ───────────────────────────
  let seedR = 0,
    seedG = 0,
    seedB = 0,
    seedN = 0;
  for (let y = ty0; y <= ty1; y++) {
    for (let x = tx0; x <= tx1; x++) {
      const o = (y * width + x) * 4;
      seedR += data[o];
      seedG += data[o + 1];
      seedB += data[o + 2];
      seedN++;
    }
  }
  if (seedN > 0) {
    seedR /= seedN;
    seedG /= seedN;
    seedB /= seedN;
  }

  const matches = (x: number, y: number): boolean => {
    const o = (y * width + x) * 4;
    const cd = Math.max(
      Math.abs(data[o] - seedR),
      Math.abs(data[o + 1] - seedG),
      Math.abs(data[o + 2] - seedB),
    );
    return cd <= colorTolerance;
  };

  // ── Step 4: grow outward from the TRUST rect ─────────────────────────────
  // Each direction keeps looking up to MAX_FAIL_STREAK rows past a bad
  // strip. A single textline or shadow band doesn't permanently stop
  // expansion — only a run of bad strips (real paper edge + beyond) does.
  // Size it as ~5% of the smaller axis so it scales with frame resolution.
  const MAX_FAIL_STREAK = Math.max(
    3,
    Math.floor(Math.min(width, height) * 0.05),
  );

  let bx0 = tx0,
    by0 = ty0,
    bx1 = tx1,
    by1 = ty1;

  // Top — march up from the trust rect's top edge
  {
    let fails = 0;
    for (let y = ty0 - 1; y >= 0; y--) {
      let m = 0;
      for (let x = bx0; x <= bx1; x++) if (matches(x, y)) m++;
      if (m / (bx1 - bx0 + 1) < stripAcceptRatio) {
        if (++fails > MAX_FAIL_STREAK) break;
      } else {
        by0 = y;
        fails = 0;
      }
    }
  }
  // Bottom
  {
    let fails = 0;
    for (let y = ty1 + 1; y < height; y++) {
      let m = 0;
      for (let x = bx0; x <= bx1; x++) if (matches(x, y)) m++;
      if (m / (bx1 - bx0 + 1) < stripAcceptRatio) {
        if (++fails > MAX_FAIL_STREAK) break;
      } else {
        by1 = y;
        fails = 0;
      }
    }
  }
  // Left — uses the already-expanded vertical range so a tall strip sees
  // the full height of the paper when deciding whether to keep growing.
  {
    let fails = 0;
    for (let x = tx0 - 1; x >= 0; x--) {
      let m = 0;
      for (let y = by0; y <= by1; y++) if (matches(x, y)) m++;
      if (m / (by1 - by0 + 1) < stripAcceptRatio) {
        if (++fails > MAX_FAIL_STREAK) break;
      } else {
        bx0 = x;
        fails = 0;
      }
    }
  }
  // Right
  {
    let fails = 0;
    for (let x = tx1 + 1; x < width; x++) {
      let m = 0;
      for (let y = by0; y <= by1; y++) if (matches(x, y)) m++;
      if (m / (by1 - by0 + 1) < stripAcceptRatio) {
        if (++fails > MAX_FAIL_STREAK) break;
      } else {
        bx1 = x;
        fails = 0;
      }
    }
  }

  // ── Step 5: score ────────────────────────────────────────────────────────
  const bboxW = bx1 - bx0 + 1;
  const bboxH = by1 - by0 + 1;
  const frameFill = (bboxW * bboxH) / total;

  // How far past the guide edges the bbox pushed (in pixels), relative to the
  // room available past the guide. 0 = bbox within guide; 1 = bbox at frame.
  const extraPx =
    Math.max(0, gx0 - bx0) +
    Math.max(0, bx1 - gx1) +
    Math.max(0, gy0 - by0) +
    Math.max(0, by1 - gy1);
  const availablePx = width - gw + (height - gh);
  const expansion = availablePx > 0 ? extraPx / availablePx : 0;

  // Mean gradient at the bbox boundary — sharper edge = stronger detection.
  let edgeSum = 0,
    edgeN = 0;
  for (let x = bx0; x <= bx1; x++) {
    if (by0 > 0) {
      edgeSum += grad[(by0 - 1) * width + x];
      edgeN++;
    }
    if (by1 < height - 1) {
      edgeSum += grad[(by1 + 1) * width + x];
      edgeN++;
    }
  }
  for (let y = by0; y <= by1; y++) {
    if (bx0 > 0) {
      edgeSum += grad[y * width + (bx0 - 1)];
      edgeN++;
    }
    if (bx1 < width - 1) {
      edgeSum += grad[y * width + (bx1 + 1)];
      edgeN++;
    }
  }
  const edgeContrast = edgeN > 0 ? edgeSum / edgeN : 0;

  // Detection requires the bbox to cover the guide on each edge — but with
  // a soft margin so a small shortfall (shadow, crooked page corner) doesn't
  // kill a clearly-valid capture. `guideCoverMargin` is the max fraction of
  // each guide dim that the bbox is allowed to fall short by.
  const marginX = Math.round(gw * guideCoverMargin);
  const marginY = Math.round(gh * guideCoverMargin);
  const coversGuide =
    bx0 <= gx0 + marginX &&
    by0 <= gy0 + marginY &&
    bx1 >= gx1 - marginX &&
    by1 >= gy1 - marginY;

  let score = 0;
  if (coversGuide && frameFill >= minFillRatio && expansion >= minExpansion) {
    score = Math.min(1, frameFill * 2);
  }

  const bbox = {
    x: bx0 / width,
    y: by0 / height,
    w: bboxW / width,
    h: bboxH / height,
  };
  const bboxPx = { minX: bx0, minY: by0, maxX: bx1, maxY: by1 };

  // ── Step 6: debug viz ────────────────────────────────────────────────────
  // Reading key:
  //   • Cyan dashed box       = TRUST rect (where seed was sampled)
  //   • Green dashed box      = GUIDE rect (UX target)
  //   • Solid orange box      = DETECTED bbox (paper boundary)
  //   • Inside trust rect     = original RGB passthrough (see what we sampled)
  //   • Inside bbox, outside  = green (match) / red (non-match) per pixel
  //   trust rect                 — helps diagnose stripAcceptRatio
  //   • Outside bbox          = dim gradient map (edges)
  const debugData = new Uint8ClampedArray(total * 4);
  const gradScale = 255 / Math.max(1, gradientThreshold * 4);
  for (let i = 0; i < total; i++) {
    const o = i * 4;
    const x = i % width;
    const y = (i - x) / width;
    const inTrust = x >= tx0 && x <= tx1 && y >= ty0 && y <= ty1;
    const inBbox = x >= bx0 && x <= bx1 && y >= by0 && y <= by1;
    if (inTrust) {
      debugData[o] = data[o];
      debugData[o + 1] = data[o + 1];
      debugData[o + 2] = data[o + 2];
    } else if (inBbox) {
      if (matches(x, y)) {
        debugData[o] = 74;
        debugData[o + 1] = 222;
        debugData[o + 2] = 128;
      } else {
        debugData[o] = 140;
        debugData[o + 1] = 50;
        debugData[o + 2] = 50;
      }
    } else {
      const g = Math.min(255, grad[i] * gradScale);
      debugData[o] = g;
      debugData[o + 1] = g;
      debugData[o + 2] = g;
    }
    debugData[o + 3] = 255;
  }

  const drawDashedRect = (
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    r: number,
    g: number,
    b: number,
    dashLen: number,
  ) => {
    const on = (t: number) => (Math.floor(t / dashLen) & 1) === 0;
    for (let x = x0; x <= x1; x++) {
      if (!on(x - x0)) continue;
      for (const y of [y0, y1]) {
        const o = (y * width + x) * 4;
        debugData[o] = r;
        debugData[o + 1] = g;
        debugData[o + 2] = b;
      }
    }
    for (let y = y0; y <= y1; y++) {
      if (!on(y - y0)) continue;
      for (const x of [x0, x1]) {
        const o = (y * width + x) * 4;
        debugData[o] = r;
        debugData[o + 1] = g;
        debugData[o + 2] = b;
      }
    }
  };

  // Cyan dashed trust rect (tight dashes so it reads as a distinct box)
  drawDashedRect(tx0, ty0, tx1, ty1, 80, 220, 240, 2);
  // Green dashed guide rect
  drawDashedRect(gx0, gy0, gx1, gy1, 74, 222, 128, 4);
  // Dim inner green dashed rect at the cover-margin threshold — bbox just
  // has to reach this inner box on each edge to count as covering the guide.
  if (marginX > 0 || marginY > 0) {
    const mx0 = gx0 + marginX;
    const my0 = gy0 + marginY;
    const mx1 = gx1 - marginX;
    const my1 = gy1 - marginY;
    if (mx1 > mx0 && my1 > my0) {
      drawDashedRect(mx0, my0, mx1, my1, 40, 120, 70, 3);
    }
  }
  // Solid orange bbox
  for (let x = bx0; x <= bx1; x++) {
    for (const y of [by0, by1]) {
      const o = (y * width + x) * 4;
      debugData[o] = 255;
      debugData[o + 1] = 160;
      debugData[o + 2] = 0;
    }
  }
  for (let y = by0; y <= by1; y++) {
    for (const x of [bx0, bx1]) {
      const o = (y * width + x) * 4;
      debugData[o] = 255;
      debugData[o + 1] = 160;
      debugData[o + 2] = 0;
    }
  }

  return {
    score,
    detected: score > 0,
    debugImageData: new ImageData(debugData, width, height),
    bbox,
    bboxPx,
    frameFill,
    // Expansion lives in the bboxFill slot so the debug UI renders it unchanged.
    bboxFill: expansion,
    edgeContrast,
  };
}
